/**
 * roundRobin.js — Round Robin converter math for the Parlay Builder
 * (Winning Features addendum). Pure, no I/O.
 *
 * A round robin breaks a parlay's legs into every combination of a
 * chosen subset size (by 2s, by 3s, ...), each combination becoming
 * its own parlay at the same unit stake. The per-combo math is the
 * builder's usual slip math (combined odds = product of leg decimals,
 * true chance = product of leg fair probabilities) — the same
 * formulas ParlayBuilderScreen recalculates locally and
 * parlayEngine.slipMetrics computes server-side.
 */

function decimalToAmerican(decimal) {
  if (typeof decimal !== 'number' || decimal <= 1) return null;
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

/** All combinations of `size` items from `items` (order-preserving). */
export function combinations(items, size) {
  const list = Array.isArray(items) ? items : [];
  if (!Number.isInteger(size) || size < 1 || size > list.length) return [];
  const out = [];
  const walk = (start, picked) => {
    if (picked.length === size) {
      out.push(picked.slice());
      return;
    }
    for (let i = start; i < list.length; i++) {
      picked.push(list[i]);
      walk(i + 1, picked);
      picked.pop();
    }
  };
  walk(0, []);
  return out;
}

/**
 * Build the full round robin for a slip's legs.
 *
 * legs: builder leg objects ({ decimal, fairProb, ... }).
 * sizes: subset sizes to include (each 2..legs.length). A "full"
 * round robin is every size from 2 up to legs.length.
 * unitStake: dollars risked on EACH combination.
 *
 * Returns {
 *   combos: [{ size, legs, combinedDecimal, combinedAmerican,
 *              trueChance, payout }],   // payout at the unit stake
 *   totalCombos, totalStake,           // unitStake x totalCombos
 *   maxPayout                          // sum of combo payouts (all legs hit)
 * }
 */
export function buildRoundRobin(legs, sizes, unitStake) {
  const slipLegs = Array.isArray(legs) ? legs : [];
  const stake = typeof unitStake === 'number' && unitStake > 0 ? unitStake : 0;
  const wantedSizes = [...new Set((sizes || []).filter(
    (s) => Number.isInteger(s) && s >= 2 && s <= slipLegs.length
  ))].sort((a, b) => a - b);

  const combos = [];
  for (const size of wantedSizes) {
    for (const comboLegs of combinations(slipLegs, size)) {
      let combinedDecimal = 1;
      let trueChance = 1;
      for (const leg of comboLegs) {
        combinedDecimal *= leg.decimal;
        trueChance *= leg.fairProb;
      }
      combos.push({
        size,
        legs: comboLegs,
        combinedDecimal,
        combinedAmerican: decimalToAmerican(combinedDecimal),
        trueChance,
        payout: stake * combinedDecimal
      });
    }
  }

  return {
    combos,
    totalCombos: combos.length,
    totalStake: stake * combos.length,
    maxPayout: combos.reduce((sum, combo) => sum + combo.payout, 0)
  };
}

export default buildRoundRobin;
